import React, { useMemo } from 'react';
import useSeo from '../hooks/useSeo';
import '../styles/pages/Legal.css';

function PrivacyPolicy() {
  const seoConfig = useMemo(() => {
    const title = 'BloomNav Privacy Policy | Technology Studio';
    const description =
      'How BloomNav collects, uses, and protects information across client projects, our website, and the software we build and run for clients.';
    return {
      title,
      description,
      canonicalPath: '/privacy',
      ogType: 'article',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav'
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="legal-page">
      <div className="legal-head">
        <span className="legal-kicker">Legal</span>
        <h1 className="legal-title">
          Privacy <em>policy.</em>
        </h1>
        <p className="legal-sub">
          How we handle information when you talk to us about a project, when we build software for
          you, and when you use something we run on your behalf.
        </p>
      </div>

      <section className="legal-section">
        <h2 className="legal-section-title">1. Who we are</h2>
        <div>
          <p>
            BloomNav is a technology studio operated by CLIVEXCI SYSTEMS LLP ("BloomNav," "we,"
            "our," or "us"). We design and build custom software: web and mobile apps, SaaS
            platforms, AI features, and internal systems for teams in areas like fintech, commerce,
            logistics, edtech, and operations.
          </p>
          <p>
            This policy covers our website, our client portal, and the work we do on client
            projects. If you have signed a separate agreement with us, that agreement takes
            precedence where the two differ.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">2. Information we collect</h2>
        <div>
          <h3 className="legal-sub-title">2.1 When you contact us</h3>
          <p>
            If you enquire about a project, book a call, or fill in a form, we collect your name,
            email address, phone number, company name, and whatever you tell us about the work you
            have in mind. We keep the conversation history so we can quote accurately and follow up.
          </p>

          <h3 className="legal-sub-title">2.2 Client and project accounts</h3>
          <p>
            If you work with us, we hold contact details for the people on your side, billing and
            invoicing details, and project records such as scopes, plans, estimates, sprint notes,
            issue reports, and release history.
          </p>

          <h3 className="legal-sub-title">2.3 Technical information</h3>
          <p>
            When you visit our site or sign in to our portal, we may collect standard technical data
            such as browser type, device type, approximate location derived from IP address, pages
            viewed, and error logs. We use this to keep the service working and secure.
          </p>

          <h3 className="legal-sub-title">2.4 Materials you share with us</h3>
          <p>
            To build software, you may share designs, documents, specifications, credentials, sample
            data, or access to existing systems. We treat this material as confidential and use it
            only to deliver the work you have asked for.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">3. Data inside client projects</h2>
        <div>
          <p>
            When we build or maintain software for a client, that client decides what personal data
            their product collects and why. In most cases the client is the data controller and we
            act as a service provider or processor on their instructions.
          </p>
          <ul className="legal-list">
            <li>We access client production data only when it is needed to build, debug, migrate, or support the system.</li>
            <li>We prefer anonymised or sample data for development and testing wherever practical.</li>
            <li>We do not use client data to train our own models or to build products for anyone else.</li>
            <li>We do not sell client data or the personal data of their users.</li>
          </ul>
          <div className="legal-callout">
            <p>
              <strong>If you are an end user of an app we built:</strong> the company that
              commissioned the app is responsible for its privacy policy and for answering data
              requests. Please contact them first. If you reach us instead, we will pass the request
              on to them.
            </p>
          </div>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">4. How we use information</h2>
        <div>
          <ul className="legal-list">
            <li>To answer enquiries, scope work, and prepare estimates and proposals.</li>
            <li>To deliver projects: design, development, testing, deployment, and support.</li>
            <li>To run accounts, raise invoices, and collect payment.</li>
            <li>To communicate about progress, releases, incidents, and planned work.</li>
            <li>To keep our systems secure, diagnose faults, and improve how we work.</li>
            <li>To meet legal, tax, and accounting obligations.</li>
          </ul>
          <p>
            We rely on your consent for marketing messages, on contract performance for project
            delivery, and on our legitimate interests for security, service improvement, and running
            the business responsibly.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">5. Sharing and service providers</h2>
        <div>
          <p>
            We do not sell personal information. We share it only where it is needed to run our
            business or deliver your project:
          </p>
          <ul className="legal-list">
            <li><strong>Cloud hosting and storage</strong> for application infrastructure, backups, and file storage.</li>
            <li><strong>Communication and collaboration tools</strong> for email, calls, project tracking, and notifications.</li>
            <li><strong>Payment and accounting providers</strong> for invoicing, payment processing, and bookkeeping.</li>
            <li><strong>Analytics and error monitoring</strong> to understand usage and fix faults.</li>
            <li><strong>Specialist contractors</strong> who work under confidentiality terms equivalent to ours.</li>
          </ul>
          <p>
            Where a project runs on infrastructure you own, your own providers apply and you control
            those choices. We may also disclose information where the law requires it, to protect
            our rights, or in connection with a business transfer.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">6. Security</h2>
        <div>
          <p>
            We take practical steps to protect information: encrypted transport, access limited to
            the people who need it, separate credentials for each environment, secret management
            rather than shared plaintext, and reviews before code reaches production.
          </p>
          <p>
            Credentials you give us are stored in a password manager and revoked when the engagement
            ends. If you believe a credential has been exposed, tell us and rotate it. No system is
            perfectly secure, so we cannot guarantee absolute protection.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">7. How long we keep information</h2>
        <div>
          <ul className="legal-list">
            <li><strong>Enquiries that do not become projects:</strong> kept for up to two years, then deleted.</li>
            <li><strong>Project records:</strong> kept for the engagement and for a reasonable period after, so we can support what we built.</li>
            <li><strong>Financial records:</strong> kept as long as tax and company law require.</li>
            <li><strong>Client credentials and access:</strong> revoked at handover or when the engagement ends.</li>
          </ul>
          <p>
            At the end of a project we can return or delete the material you shared with us on
            request, apart from records we must keep for legal reasons or copies held in routine
            backups until they expire.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">8. Your rights</h2>
        <div>
          <p>
            Depending on where you live, you may have the right to access the personal information
            we hold about you, correct it, ask us to delete it, restrict or object to certain uses,
            withdraw consent, or receive a copy in a portable format.
          </p>
          <p>
            To make a request, email us with enough detail to identify your records. We will respond
            within the timeframe the law allows. If we hold the data on behalf of a client, we will
            forward your request to them.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">9. International transfers</h2>
        <div>
          <p>
            We are based in India and work with clients and providers in other countries, so
            information may be processed outside your home country. Where that happens we use
            appropriate safeguards, such as contractual protections with our providers, and we
            follow any hosting or data residency requirements agreed in your contract.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">10. Children</h2>
        <div>
          <p>
            Our website and portal are meant for businesses and are not directed at children. If we
            build a product intended for younger users, the client sets the age policy for that
            product and we implement the controls agreed in the project scope.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">11. Changes to this policy</h2>
        <div>
          <p>
            We may update this policy as our services, providers, or legal obligations change. The
            date below shows the current version. For material changes affecting active clients, we
            will give notice by email or through the portal.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">12. Contact us</h2>
        <div>
          <p>Questions about this policy or how we handle information:</p>
          <p><strong>Company:</strong> CLIVEXCI SYSTEMS LLP (BloomNav)</p>
          <p><strong>Email:</strong> support@bloomnav.com</p>
          <p><strong>Subject line:</strong> Privacy enquiry</p>
          <p><strong>Location:</strong> Goa, India</p>
        </div>
      </section>

      <div className="legal-stamp">
        <p><strong>Last updated:</strong> July 2026</p>
        <p><strong>Effective date:</strong> July 2026</p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
